import { useEffect, useState } from 'react'

import { EmployeeSelectorInitialValuesDocument } from 'generated/resources/graphql'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'

interface EmployeeSelectorWithIdsProps {
  selectedEmployeeIds: string[]
  onSelect: (employees: EmployeeForSelectFragmentNoAccess[]) => void
  hideSearchIcon?: boolean
  emptyLabel?: string
}

export const EmployeeSelectorWithIds = ({
  selectedEmployeeIds,
  onSelect,
  hideSearchIcon = false,
  emptyLabel = '',
}: EmployeeSelectorWithIdsProps) => {
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeForSelectFragmentNoAccess[]>(
    []
  )
  const { company } = useCurrentSession()

  // Convert string IDs to numbers for the GraphQL query
  const employeeIds = selectedEmployeeIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id))

  // Fetch employee data when IDs change
  const { data, loading } = useQuery(EmployeeSelectorInitialValuesDocument, {
    variables: {
      companyId: company.id,
      ids: employeeIds,
    },
    skip: employeeIds.length === 0,
  })

  // Update selected employees when data is fetched
  useEffect(() => {
    if (data?.employees?.employeeNames) {
      setSelectedEmployees(data.employees.employeeNames)
    } else if (employeeIds.length === 0) {
      setSelectedEmployees([])
    }
  }, [data, employeeIds.length])

  const handleSelect = (employees: EmployeeForSelectFragmentNoAccess[]) => {
    setSelectedEmployees(employees)
    onSelect(employees)
  }

  return (
    <EmployeeSelector
      hideSearchIcon={hideSearchIcon}
      emptyLabel={emptyLabel}
      multiple
      onSelect={handleSelect}
      value={selectedEmployees}
      disabled={loading}
      allowedGroups={['all', 'teams', 'managers', 'workplaces']}
    />
  )
}
